import React from 'react';
import { cn } from 'src/lib/utils';
import { STATUS_PILL_BASE, STATUS_SCHEMES } from 'src/lib/statusStyles';

/**
 * Extract the base color name from a palette-key string like "indigo-50" → "indigo".
 * Returns null for raw hex / CSS-var values.
 */
function extractColorName(colorStr) {
  if (!colorStr) return null;
  if (
    colorStr.startsWith('#') ||
    colorStr.startsWith('rgb') ||
    colorStr.startsWith('var(')
  ) {
    return null;
  }
  const match = /^(\w+)-\d+/.exec(String(colorStr).trim());
  return match ? match[1] : null;
}

/**
 * Resolve a palette-key string ("indigo-50") to a hex value for the inline-style fallback.
 */
const PALETTE = {
  slate: { 50: '#F8FAFC', 200: '#E2E8F0', 600: '#475569' },
  blue: { 50: '#E8F3FD', 200: '#BFDCF5', 700: '#1B5CA0' },
  amber: { 50: '#FFFBEB', 200: '#FDE68A', 700: '#B45309' },
  indigo: { 50: '#EEF2FF', 200: '#C7D2FE', 600: '#4F46E5' },
  teal: { 50: '#F0FDFA', 200: '#99F6E4', 700: '#0F766E' },
  violet: { 50: '#F5F3FF', 200: '#DDD6FE', 700: '#6D28D9' },
  emerald: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#10B981',
    600: '#059669',
    700: '#047857',
    800: '#065F46',
    900: '#064E3B',
  },
  red: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },
};

function resolveColor(input) {
  if (!input) return undefined;
  if (
    input.startsWith('#') ||
    input.startsWith('rgb') ||
    input.startsWith('hsl') ||
    input.startsWith('var(')
  ) {
    return input;
  }
  const match = /^(\w+)-(50|100|200|300|400|500|600|700|800|900)$/.exec(
    String(input).trim()
  );
  if (match) {
    const [, name, shade] = match;
    const shades = PALETTE[name];
    if (shades && shades[shade]) return shades[shade];
  }
  return input;
}

function StatusPill({
  name,
  icon = null,
  /** Explicit color scheme name, e.g. "indigo", "emerald", "red" */
  colorScheme,
  borderColor,
  textColor,
  bgColor,
  className,
  style,
  ...props
}) {
  // Resolve scheme: explicit prop first, then auto-detect from bgColor palette key
  const schemeName = colorScheme || extractColorName(bgColor);
  const scheme = schemeName ? STATUS_SCHEMES[schemeName] : null;

  // Only fall back to inline styles when no shared scheme is available
  // (e.g. raw hex values passed by the caller).
  const mergedStyle = scheme
    ? style
    : {
        borderColor: resolveColor(borderColor),
        color: resolveColor(textColor),
        backgroundColor: resolveColor(bgColor),
        ...style,
      };

  // Dot color: scheme class, or the caller's text/border color for the raw path.
  const dotStyle = scheme
    ? undefined
    : { backgroundColor: resolveColor(textColor) || resolveColor(borderColor) };

  return (
    <span
      className={cn(STATUS_PILL_BASE, scheme?.pill, className)}
      style={mergedStyle}
      {...props}
    >
      {icon ? (
        <span className="flex items-center" aria-hidden>
          {icon}
        </span>
      ) : (
        <span
          className={cn('h-1.5 w-1.5 rounded-full shrink-0', scheme?.dot)}
          style={dotStyle}
          aria-hidden
        />
      )}
      <span>{name}</span>
    </span>
  );
}

export { StatusPill };
